import { LitElement, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '@maersk-global/mds-components-core'
import { Status } from '../types/polar-chart-types'
import { polarLoadingModalStyles } from '../style/polar-loading-modal-styles'
import { dispatchTogglePolarPlotMarkerEvent } from '../helper'
import { PolarPlotService } from '../../../src/services/polar-plot-service'
import { getRequestIdFromErrorReport, buildPolarPlotFailureSummary } from '../utils/header-utils'

export enum PolarLoadingModalEventNames {
    ClosePolarChartLoader = 'close-polarplot-loader',
    RetryCalculation = "polarplot-retry-calculation",
}

@customElement('polar-loading-modal')
export class PolarLoadingModal extends LitElement {
    @property({ type: String }) status: Status = ''
    @property({ type: String }) errorLog: string = ''
    @property({ type: String }) customErrorText: string | null = null
    @property({ attribute: false }) errorReport: any = null



    static styles = polarLoadingModalStyles

    private downloadJson(data: any, filename: string) {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    private getRequestId(): string | undefined {
        return getRequestIdFromErrorReport(this.errorReport);
    }

    private async downloadErrorJson() {
        if (!this.errorReport) {
            alert('No error information available to download');
            return;
        }

        try {
            let requestId: string | undefined;
            try {
                requestId = this.getRequestId();
            } catch {
                requestId = undefined;
            }

            const summary = buildPolarPlotFailureSummary(this.errorReport, requestId);

            if (requestId) {
                try {
                    const auditLog = await PolarPlotService.fetchAuditLog(requestId);
                    this.downloadJson(
                        {
                            polarplot_failure_summary: summary,
                            audit_log: auditLog,
                            full_error_report: this.errorReport,
                        },
                        `polarplot-audit-log-${requestId}-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
                    );
                    return;
                } catch {
                    // Audit log endpoint failed; fall through to download with summary + full error only
                }
            }

            // No request_id, or audit fetch failed: always include error_type and full error in download
            this.downloadJson(
                {
                    polarplot_failure_summary: summary,
                    full_error_report: this.errorReport,
                },
                `polarplot-error-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
            );
        } catch (downloadError) {
            console.error('Download error:', downloadError);
            alert('Failed to download error report.');
        }
    }

    render() {
        // Always show the same generic message so fleet and vessel are consistent; do not show error_type or technical details (they are in the downloaded report).
        const displayCustomError =
            "It seems like we encountered an error. Please click on the Try Again below."

        return html`
            <mc-modal
                heading="Polar Chart Advice"
                zindex="99999"
                height="457px"
                hiddenclose
                backdropcloseactiondisabled
                escapecloseactiondisabled
                entercloseactiondisabled
                ?open=${this.status !== 'success'}
            >
                <div class="loader-content">
                    ${this.status === 'error'
                        ? html`
                              <img
                                  src="./assets/images/ship-night-loader.svg"
                                  class="loading-img"
                              />
                              <p>
                                  <span class="error-text-block">
                                      ${displayCustomError || "It seems like we encountered an error. Please click on the Try Again below."} </span
                                  >
                                  <span class="error-text">
                                      If this keeps happening,
                                      <button
                                          type="button"
                                          class="copy-button"
                                          @click=${this.downloadErrorJson}
                                      >
                                          Download the error report
                                      </button>
                                      and send it attached in an e-mail support
                                      ticket. We will try to fix it as soon as
                                      possible.
                                  </span>
                              </p>
                          `
                        : html`<img
                                  src="./assets/images/ship-loader.gif"
                                  class="loading-img"
                              />
                              <p class="loading-text">
                                  Please wait while we load Polar Chart for
                                  you... 
                              </p>`}
                </div>

                ${this.status === 'error'
                    ? html`<mc-button
                          slot="primaryAction"
                          appearance="primary"
                          fit="medium"
                          @click="${() => {
                              this.dispatchEvent(
                                  new Event(
                                      PolarLoadingModalEventNames.RetryCalculation,
                                  ),
                              )
                          }}"
                          >Try Again
                      </mc-button>`
                    : null}
                <mc-button
                    slot="secondaryAction"
                    appearance="neutral"
                    variant="filled"
                    dialogaction="cancel"
                    fit="medium"
                    @click=${() => {
                        this.dispatchEvent(
                            new Event(
                                PolarLoadingModalEventNames.ClosePolarChartLoader,
                                {
                                    bubbles: true,
                                    composed: true,
                                },
                            ),
                        );
                        dispatchTogglePolarPlotMarkerEvent({showMarker: false, position: null, nearestEta: null});
                    }}
                >
                    Cancel
                </mc-button>
            </mc-modal>
        `
    }
}
